import React from "react"

import {
  deriveYesNoString,
  StringValueYesNoChip,
} from "@/metrics/metric-display"
import { SpectrogramPitchAnalysisConfig } from "@/metrics/registry/configuration"
import type { AudioRegionReviewConfig } from "@/metrics/runtime"

import type { AudioRegionParams } from "@/components/audio-player/AudioPlayer"
import type { TestCaseRecord } from "@/types/api/TestCaseRecord"
import type { MetricRecordData } from "@/types/metrics/contracts"
import type {
  MetricOutputDisplaySource,
  RunMetricOutput,
} from "@/types/RunMetricOutput"

import { AggregateOverviewFloat } from "../../components/aggregate/AggregateOverviewFloat"
import { NumericalMetricRecord } from "../base/NumericalMetricRecord"
import { WithAudioGateMixin } from "../mixins/WithAudioGateMixin"

type BaseTestCaseRecord = Pick<
  TestCaseRecord,
  "input_str" | "expected_behaviors" | "expected_output_json"
>

const CATEGORY = "spectrogram_pitch_analysis"

export class SpectrogramPitchAnalysisMetricRecord extends WithAudioGateMixin(
  NumericalMetricRecord,
) {
  overviewSize = "small" as const

  getSingleMetricOutputNode(mo: RunMetricOutput): React.ReactNode {
    // Suppress the chip when the metric could not classify the audio.
    if (deriveYesNoString(mo) === "UNKNOWN") {
      return null
    }
    const targetCondition = this?.metric_metadata?.target_condition
    return <StringValueYesNoChip mo={mo} targetCondition={targetCondition} />
  }

  getHeaderValue(): string | null {
    return null
  }

  getOverviewNode(mos: RunMetricOutput[]): React.ReactNode {
    return <AggregateOverviewFloat mos={mos} units={this.aggregate_units} />
  }

  renderInDepthRunReview(metricOutputs: RunMetricOutput[]): React.ReactNode {
    if (metricOutputs.length === 0) {
      return (
        <div className="text-coval-text-muted">
          No metric data available for this metric.
        </div>
      )
    }
    return (
      <AggregateOverviewFloat
        mos={metricOutputs}
        units={this.aggregate_units}
      />
    )
  }

  _renderDeepDive(
    _mo: RunMetricOutput,
    _testCase: BaseTestCaseRecord,
    _dispatchMessageSelect?: (messageIndex: number | null) => void,
  ): React.ReactNode {
    return null
  }

  getAudioRegions(_mo: MetricOutputDisplaySource): AudioRegionParams[] {
    return []
  }

  getAudioRegionReviewConfig(): AudioRegionReviewConfig | null {
    return {
      category: CATEGORY,
      scoreType: "seconds",
      buildReviewedResultJson: ({ score, durationSeconds }) => ({
        average_fill_ratio: durationSeconds
          ? Number((score / durationSeconds).toFixed(4))
          : 0,
        duration_seconds:
          durationSeconds != null ? Number(durationSeconds.toFixed(2)) : null,
      }),
    }
  }

  renderEditorMetadata(
    metricRecordData: MetricRecordData,
    onChange: (metric: MetricRecordData) => void,
    textAreaRef?: React.RefObject<HTMLTextAreaElement>,
  ): React.ReactNode {
    return (
      <div className="flex flex-col gap-4">
        <SpectrogramPitchAnalysisConfig
          metricRecordData={metricRecordData}
          onChange={onChange}
        />
        {super.renderEditorMetadata(metricRecordData, onChange, textAreaRef)}
      </div>
    )
  }
}
